/*
 * Sunbrella Studio custom scripts.
 * Loaded as a module after theme.js (layout/theme.liquid).
 * Keep customizations here instead of editing theme.js, which is replaced on Prestige updates.
 */

/*
 * Mega menus (SB): the images are lazy loaded and the menus stay hidden until opened, so they only started loading when
 * a menu opened. Once the page has loaded, load the images of the menu shown at this screen size in the background:
 * the mega menus on desktop (from 1000px) and the sidebar thumbnails on mobile.
 */
const desktopMediaSB = window.matchMedia('(min-width: 1000px)');

const loadMenuImagesSB = () => {
  const selector = desktopMediaSB.matches ? '.mega-menu-SB img[loading="lazy"]' : '.menu-link-SB__thumb img[loading="lazy"]';

  document.querySelectorAll(selector).forEach((image) => {
    image.loading = 'eager';
  });
};

const scheduleMenuImagesSB = () => {
  'requestIdleCallback' in window ? requestIdleCallback(loadMenuImagesSB, { timeout: 3000 }) : setTimeout(loadMenuImagesSB, 1000);
};

document.readyState === 'complete' ? scheduleMenuImagesSB() : window.addEventListener('load', scheduleMenuImagesSB, { once: true });
desktopMediaSB.addEventListener('change', scheduleMenuImagesSB);

/*
 * Product cards (SB): <swatch-list-sb> (snippets/product-card.liquid) shows at most 2 rows of color swatches. How many
 * fit in a row depends on the card width, so they are counted after layout (and again when the card is resized). The
 * "X More" link takes the place of the last swatches of the second row, so it ends where the first row ends.
 */
class SwatchListSB extends HTMLElement {
  connectedCallback() {
    this.swatches = [...this.querySelectorAll('.color-swatch')];
    this.moreLink = this.querySelector('.swatch-list-SB__more');

    if (this.swatches.length > 2 && this.moreLink) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  update() {
    if (this.offsetWidth === this.lastWidth) return;
    this.lastWidth = this.offsetWidth;

    const { swatches, moreLink } = this;

    swatches.forEach((swatch) => (swatch.style.display = ''));
    moreLink.hidden = true;
    moreLink.style.width = '';

    const perRow = swatches.findIndex((swatch) => swatch.offsetTop !== swatches[0].offsetTop);

    if (perRow < 2 || swatches.length <= perRow * 2) return;

    // A slot is one swatch and the gap after it; the link takes as many slots as its text needs
    const slot = swatches[1].offsetLeft - swatches[0].offsetLeft;
    const gap = slot - swatches[0].offsetWidth;
    let linkSlots = 0;
    let neededSlots = 1;

    moreLink.hidden = false;

    while (neededSlots > linkSlots && neededSlots <= perRow) {
      linkSlots = neededSlots;
      moreLink.textContent = this.dataset.moreText.replace('[count]', swatches.length - (perRow * 2 - linkSlots));
      neededSlots = Math.ceil((moreLink.offsetWidth + gap) / slot);
    }

    swatches.forEach((swatch, index) => (swatch.style.display = index < perRow * 2 - linkSlots ? '' : 'none'));
    moreLink.style.width = `${linkSlots * slot - gap}px`;
  }
}

customElements.define('swatch-list-sb', SwatchListSB);

/*
 * Product page (SB): <swatch-rows-sb> (snippets/variant-picker.liquid) cuts the color swatches at 2 rows, with a
 * "Show All" / "Show Less" button.
 */
class SwatchRowsSB extends HTMLElement {
  static expandedOptions = new Set();

  connectedCallback() {
    this.values = this.querySelector('.variant-picker__option-values');
    this.toggleButton = this.querySelector('.swatch-rows-SB__toggle');
    this.expanded = SwatchRowsSB.expandedOptions.has(this.values.id);

    this.toggleButton.addEventListener('click', () => (this.expanded = !this.expanded));

    // Keyboard users moving through the colors with the arrow keys can reach the hidden ones: show them
    this.values.addEventListener('focusin', (event) => this.expandIfHidden(event.target.nextElementSibling));

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get expanded() {
    return this.hasAttribute('expanded');
  }

  set expanded(value) {
    this.toggleAttribute('expanded', value);
    this.toggleButton.setAttribute('aria-expanded', value);
    value ? SwatchRowsSB.expandedOptions.add(this.values.id) : SwatchRowsSB.expandedOptions.delete(this.values.id);
  }

  // Top of the third row of swatches, the first one cut when collapsed (undefined when there are only 2 rows). Sold out
  // colors can be hidden (display: none), so they are left out
  get thirdRowTop() {
    const swatches = [...this.values.querySelectorAll('.color-swatch')].filter((swatch) => swatch.offsetParent);
    return [...new Set(swatches.map((swatch) => swatch.offsetTop))][2];
  }

  expandIfHidden(swatch) {
    const thirdRowTop = this.thirdRowTop;

    if (!this.expanded && swatch && thirdRowTop !== undefined && swatch.offsetTop >= thirdRowTop) {
      this.expanded = true;
    }
  }

  update() {
    if (this.offsetWidth === this.lastWidth) return;
    this.lastWidth = this.offsetWidth;

    this.toggleButton.hidden = this.thirdRowTop === undefined;

    // The selected color (e.g. from a ?variant= link) must be visible
    this.expandIfHidden(this.values.querySelector(':checked + .color-swatch'));
  }
}

customElements.define('swatch-rows-sb', SwatchRowsSB);

/*
 * Product page (SB): <product-details-sb> shows the selected color in its heading.
 * Prestige only re-renders the main product section when the color changes, so this section is fetched again for the
 * new variant and its content swapped in.
 * Only one accordion is open at a time: opening one closes the others.
 * Links with data-check-url (the fabric PDF spec sheet) are hidden until a HEAD request confirms the file exists (the
 * PDF server allows it: access-control-allow-origin: *). Results are cached, so switching colors back is instant.
 */
class ProductDetailsSB extends HTMLElement {
  static checkedUrls = new Map();

  connectedCallback() {
    this.checkLinks();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener('variant:change', (event) => this.onVariantChange(event), { signal });

    // "toggle" doesn't bubble, so it's caught on the way down
    this.addEventListener('toggle', (event) => this.onAccordionToggle(event), { capture: true, signal });
  }

  checkLinks() {
    this.querySelectorAll('a[data-check-url][hidden]').forEach(async (link) => {
      if (!ProductDetailsSB.checkedUrls.has(link.href)) {
        ProductDetailsSB.checkedUrls.set(link.href, fetch(link.href, { method: 'HEAD' }).then((response) => response.ok, () => false));
      }

      link.hidden = !(await ProductDetailsSB.checkedUrls.get(link.href));
    });
  }

  get accordions() {
    return [...this.querySelectorAll('accordion-disclosure')];
  }

  onAccordionToggle(event) {
    if (!event.target.matches?.('details[open]')) return;

    // close() is Prestige's accordion API (theme.js), which animates it
    this.accordions.forEach((accordion) => {
      if (!accordion.contains(event.target) && accordion.disclosureElement?.open) accordion.close();
    });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  async onVariantChange(event) {
    const variantId = event.detail?.variant?.id;
    // form.id would return the form's "id" input (the variant ID), hence getAttribute
    const formId = event.target.getAttribute?.('id') ?? '';

    // The main product form is "product-form-main-{product ID}-{section ID}" (sections/main-product.liquid)
    if (!variantId || !formId.startsWith(`product-form-main-${this.getAttribute('product-id')}-`)) return;

    this.fetchController?.abort();
    this.fetchController = new AbortController();

    try {
      const response = await fetch(`${window.location.pathname}?variant=${variantId}&section_id=${this.getAttribute('section-id')}`, { signal: this.fetchController.signal });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newContent = html.querySelector('product-details-sb');

      if (!newContent) return;

      // Keep the accordion the customer opened (by its position) open in the new content
      const openIndex = this.accordions.findIndex((accordion) => accordion.disclosureElement?.open);

      this.replaceChildren(...newContent.childNodes);
      this.checkLinks();

      this.accordions.forEach((accordion, index) => {
        const details = accordion.querySelector('details');
        details.open = index === openIndex;
        details.setAttribute('aria-expanded', details.open);
      });
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
  }
}

customElements.define('product-details-sb', ProductDetailsSB);
